import { DecimalPipe, isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, PLATFORM_ID } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ArchiveSize } from '@nusign/picflow-backend-types';
import { combineLatest, distinctUntilChanged, firstValueFrom, map, switchMap } from 'rxjs';
import { convertGalleryDownloadSize } from '../../../lib/convert-gallery-download-size';
import { ArchiveService } from '../../../services/archive.service';
import { GalleryService } from '../../../services/gallery.service';
import { CircleProgressBarComponent } from '../../../shared/components/circle-progress-bar/circle-progress-bar.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { AnimatedSvgComponent } from '../../../shared/components/svg/animated-svg.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';

@Component({
  selector: 'app-gallery-download-page',
  imports: [
    SvgComponent,
    ButtonDirective,
    DataUnitsBigIntPipe,
    DecimalPipe,
    LoadingIndicatorComponent,
    CircleProgressBarComponent,
    AnimatedSvgComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gallery-download-page.component.html',
  styleUrl: './gallery-download-page.component.scss',
})
export class GalleryDownloadPageComponent {
  private route = inject(ActivatedRoute);
  private galleryService = inject(GalleryService);
  public archiveService = inject(ArchiveService);
  public isServer = isPlatformServer(inject(PLATFORM_ID));
  readonly gallery = toSignal(this.route.parent.data.pipe(map(data => data.gallery)));

  readonly archive = toSignal(
    toObservable(this.archiveService.items).pipe(
      map(items => items?.[0]),
      distinctUntilChanged((a, b) => {
        if (!b) return true; // we dont want to remove the last archive item (it is automatically removed after a few seconds)
        return false; // we always want to update the archive item in the UI
      })
    )
  );

  private readonly subGalleries = toSignal(
    toObservable(this.gallery).pipe(switchMap(gallery => this.galleryService.getSubGalleries(gallery.id)))
  );

  private readonly downloadSize = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const enabledSizes = convertGalleryDownloadSize(gallery.download_media_size);
    const perferenceList: ArchiveSize[] = ['original', 'medium', 'large', 'small'];
    return perferenceList.find(size => enabledSizes[size]);
  });

  private requests = toSignal(
    combineLatest([toObservable(this.gallery), toObservable(this.subGalleries)]).pipe(
      switchMap(([gallery, subGalleries]) => {
        if (!gallery || !subGalleries) return [];

        const sizeToUse = this.downloadSize();
        if (!sizeToUse) return [];

        return this.archiveService.createGalleryArchives(
          [gallery, ...subGalleries.filter(g => g.download_full_gallery)],
          sizeToUse
        );
      })
    )
  );

  readonly canDownload = computed(() => {
    const gallery = this.gallery();
    const downloadSize = this.downloadSize();
    if (!gallery || !downloadSize) return false;
    if (!gallery.download_full_gallery) return false;
    return !!this.downloadSize();
  });

  readonly totalSize = computed(() => {
    const requests = this.requests();
    if (!requests) return;
    return requests.reduce((acc, request) => acc + request.totalSize, 0n);
  });

  readonly assetCount = computed(() => {
    const requests = this.requests();
    if (!requests) return;
    return requests.reduce((acc, request) => acc + request.assetCount, 0);
  });

  async startDownload() {
    const requests = this.requests();
    if (!requests || requests.length === 0) return;
    await Promise.all(
      requests.map(async (request, index) => {
        const archive = await firstValueFrom(
          this.archiveService.requestArchiveCreation(
            request.request,
            this.gallery().id,
            request.assetCount,
            request.totalSize,
            requests.length === 1 ? undefined : { index, total: requests.length },
            true
          )
        );
        await firstValueFrom(this.archiveService.onArchiveDone(archive));
      })
    );
  }
}
